import { useForm, zodResolver } from "@mantine/form";
import {
	useGrantChatPermission,
	useRevokeChatPermission,
} from "@src/components/agent-chat/mutations/chat-mutations";
import {
	chatQueryKeys,
	useMyChat,
} from "@src/components/agent-chat/queries/chat-queries";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import {
	ChatPermission,
	UserActivationStatus,
	type WhiteboardPermission,
} from "@src/gql/graphql";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { z } from "zod";
import { useAuthenticatedUser } from "../user/user-context";
import {
	chatPermissionToWhiteboardPermission,
	getChatShareLink,
	whiteboardPermissionToChatPermission,
} from "./chat-share-utils";
import { useCopyTooltip } from "./hooks/use-copy-tooltip";
import { useSyncCollaboratorsUpdateToForm } from "./hooks/use-sync-collaborators";
import { InviteFormLayout } from "./invite-form-layout";
import {
	detectUserChanges,
	executePermissionUpdates,
	generatePermissionUpdates,
} from "./permission-updates";
import {
	checkQuotaExceeded,
	organizeUsers,
	processEmailInput,
	processNewUsers,
} from "./user-processing";
import { getUserFromCollaborator } from "./whiteboard-invite-fns";
import { inviteFormSchema } from "./whiteboard-invite-schema";

export function ShareChatInviteForm(props: { chatId: string }) {
	const authenticatedUser = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const triggerCopyTooltip = useCopyTooltip();
	const [isCopyLinkTooltipOpen, setIsCopyLinkTooltipOpen] = useState(false);

	const chatEntityId = useMemo(
		() => parseChatDocumentId(props.chatId),
		[props.chatId],
	);
	const typedChatId = chatEntityId.toString();
	const rawChatId = chatEntityId.toIdString();

	const { data: chatData } = useMyChat(rawChatId);

	const { mutateAsync: grantChatPermission } = useGrantChatPermission();
	const { mutateAsync: revokeChatPermission } = useRevokeChatPermission();

	const collaborators = useMemo(() => {
		if (!chatData) return [];
		return (chatData.collaboration?.collaborators ?? []).map((collab) => {
			const email = collab.userEmail || collab.userId;
			return {
				userId: collab.userId || email,
				userEmail: email,
				permission: chatPermissionToWhiteboardPermission(collab.permission),
				details: {
					displayName: email.split("@")[0],
					email,
					avatarUrl: "",
					activationStatus: UserActivationStatus.Registered,
				},
			};
		});
	}, [chatData]);

	const form = useForm<z.infer<typeof inviteFormSchema>>({
		mode: "controlled",
		initialValues: {
			selectedRole: "Editor",
			emails: "",
			users: collaborators.map(getUserFromCollaborator),
		},
		validate: zodResolver(inviteFormSchema),
	});

	useSyncCollaboratorsUpdateToForm(form, collaborators);

	const handleCopyLinkToClipboard = () => {
		const shareKey = chatData?.shareKey ?? "";
		void navigator.clipboard.writeText(getChatShareLink(typedChatId, shareKey));
		triggerCopyTooltip(setIsCopyLinkTooltipOpen);
	};

	const grantPermissionFn = useCallback(
		async ({
			entityId,
			userEmails,
			permission,
		}: {
			entityId: string;
			userEmails: string[];
			permission: WhiteboardPermission;
		}) => {
			const chatId = parseChatDocumentId(entityId).toString();
			await Promise.all(
				userEmails.map((email) =>
					grantChatPermission({
						chatId,
						targetUserId: email,
						permission: whiteboardPermissionToChatPermission(permission),
					}),
				),
			);
		},
		[grantChatPermission],
	);

	const revokePermissionFn = useCallback(
		async ({ entityId, userEmail }: { entityId: string; userEmail: string }) =>
			revokeChatPermission({
				chatId: parseChatDocumentId(entityId).toString(),
				targetUserId: userEmail,
			}),
		[revokeChatPermission],
	);

	const transferFn = useCallback(
		async ({
			entityId,
			newOwnerEmail,
		}: { entityId: string; newOwnerEmail: string }) =>
			grantChatPermission({
				chatId: parseChatDocumentId(entityId).toString(),
				targetUserId: newOwnerEmail,
				permission: ChatPermission.Owner,
			}),
		[grantChatPermission],
	);

	const handleSubmit = form.onSubmit(async (values) => {
		const newUsers = processEmailInput(values.emails, values.selectedRole);

		const { sortedUsers, quotaExceeded } = organizeUsers(
			values.users,
			newUsers,
			authenticatedUser.email,
		);

		if (checkQuotaExceeded(quotaExceeded, 0, form)) {
			return;
		}

		form.setFieldValue("users", sortedUsers);

		await processNewUsers(
			newUsers,
			typedChatId,
			values.selectedRole,
			grantPermissionFn,
			form,
			handleCopyLinkToClipboard,
			"chat",
		);

		const { changedUsers, removedUsers } = detectUserChanges(
			form.values.users,
			values.users,
		);

		const promises = generatePermissionUpdates(
			changedUsers,
			removedUsers,
			typedChatId,
			grantPermissionFn,
			revokePermissionFn,
			transferFn,
		);

		await executePermissionUpdates(promises);

		void queryClient.invalidateQueries({
			queryKey: chatQueryKeys.myChat(rawChatId),
		});
	});

	return (
		<InviteFormLayout
			form={form}
			handleSubmit={handleSubmit}
			entityName={chatData?.name ?? "Chat"}
			copyLinkLabel="Chat Link"
			isCopyLinkTooltipOpen={isCopyLinkTooltipOpen}
			onCopyLink={handleCopyLinkToClipboard}
		/>
	);
}
